interface StepState {
  [key: string]: {
    current: number
    context?: string
    stepContextDismissed?: boolean
  }
}

export const steps = {
  LABELS: 1,
  CREDENTIALS: 2,
  POLLING: 3,
  ANALYSIS: 4,
} as const

type Step = typeof steps[keyof typeof steps]

export const stepState = reactive<StepState>({})
export const errorState = reactive<Record<string, { title: string, text: string | string[] } | null>>({})

export function setLastError(id: string, error: { title: string, text: string | string[] }) {
  errorState[id] = error
}

export function clearLastError(id: string) {
  errorState[id] = null
}

export function handleStepChange(id: Ref<string>) {
  return (step: Step, context?: string) => {
    if (step < 1 || step > Object.keys(steps).length)
      return

    stepState[id.value] = {
      ...stepState[id.value],
      current: step,
      context,
      ...(context !== undefined ? { stepContextDismissed: false } : {}),
    }
  }
}

export function handleStepNext(id: Ref<string>) {
  return () => {
    handleStepChange(id)(stepState[id.value].current + 1 as Step)
  }
}

export function handleStepPrev(id: Ref<string>) {
  return () => {
    handleStepChange(id)(stepState[id.value].current - 1 as Step)
  }
}

export function handleStepReset(id: Ref<string>) {
  return () => {
    handleStepChange(id)(0 as Step)
  }
}

export function clearStepContext(id: string) {
  if (stepState[id]) {
    stepState[id] = {
      ...stepState[id],
      context: undefined,
      stepContextDismissed: true,
    }
  }
}

export default function useInstallationFlow(id: Ref<string>, initialState?: Partial<StepState>) {
  // Initialize the step state for the given id
  // This is done to ensure that the step state is reactive and can be used in the template
  onMounted(() => {
    if (!stepState[id.value]) {
      stepState[id.value] = {
        ...initialState,
        current: steps.LABELS,
      }
    }
  })

  return {
    stepState: computed(() => stepState[id.value]),
    errorState: computed(() => errorState[id.value]),
    setLastError: (error: { title: string, text: string | string[] }) => setLastError(id.value, error),
    clearLastError: () => clearLastError(id.value),
    handleStepChange: handleStepChange(id),
    handleStepNext: handleStepNext(id),
    handleStepPrev: handleStepPrev(id),
    handleStepReset: handleStepReset(id),
    clearStepContext: () => clearStepContext(id.value),
  }
}
